import React, {useState, ChangeEvent, KeyboardEvent} from 'react';
import {I18n} from 'react-redux-i18n';

import {Card, IconButton, withStyles, WithStyles} from '@material-ui/core';
import CachedIcon from '@material-ui/icons/Cached';
import ReactTable, {CellProps} from 'react-table';
import {CustomButton, CustomInput} from '../../material-dashboard-pro-react/components';

import ReportDomainModal from '../ReportDomainModal/ReportDomainModal';

import {CategoryHistoryResult, DomainLookupResult} from '../../types/Lookup';

import {style} from './style';

type DomainLookupProps = WithStyles<typeof style> & {
  // eslint-disable-next-line no-unused-vars
  lookupDomain: (domain: string) => void;
  categoryHistory: CategoryHistoryResult;
  lookupDomainResult?: DomainLookupResult;
  domainSearch: string;
  // eslint-disable-next-line no-unused-vars
  setDomainSearch: (domain: string) => void;
};

const defaultHistoryPageSize = 3;

function SingleElementInfo({
  classes,
  title,
  children,
  testId,
}: WithStyles<typeof style> & {title: string, children: string, testId: string}) {
  return (
    <div className={classes.singleElement}>
      <h2 className={classes.singleElement__header}>
        {title}
      </h2>
      <span className={classes.singleElement__value} data-testid={testId}>{children}</span>
    </div>
  );
}

function DomainLookup({
  classes,
  lookupDomain,
  categoryHistory,
  lookupDomainResult,
  domainSearch,
  setDomainSearch,
}: DomainLookupProps) {
  const [isReportOpen, setIsReportOpen] = useState(false);

  const UNKNOWN = I18n.t('lookupTool.unknownCategory');
  const getThreatClass = (threats: string[] | undefined) => {
    if (threats) {
      return threats?.length ?
        classes.infoGroup_hasThreats :
        classes.infoGroup_hasNoThreats;
    }
    return '';
  };

  const categoriesRenderer = (row: CellProps<String[], string[]>) => row.value.join(', ');

  return (
    <Card className={classes.mainContainer}>
      <div className={classes.mainContainer__row}>
        <CustomInput
          labelText={I18n.t('lookupTool.searchLabel')}
          id="domain-search"
          formControlProps={{
            fullWidth: true,
          }}
          inputProps={{
            'value': domainSearch,
            'name': 'domainSearch',
            'data-testid': 'lookup-domain-search-input',
            'onChange': (e: ChangeEvent<HTMLInputElement>) => setDomainSearch(e.target.value),
            'onKeyDown': (e: KeyboardEvent<HTMLInputElement>) =>
              e.key === 'Enter' && lookupDomain(domainSearch),
          }}
        />
        <CustomButton
          color="secondary"
          disabled={domainSearch.length === 0}
          onClick={() => lookupDomain(domainSearch)}
          data-testid="lookup-search-domain"
        >
          {I18n.t('lookupTool.searchButton')}
        </CustomButton>
      </div>

      <div className={classes.mainContainer__row}>
        <CustomButton
          disabled={!lookupDomainResult}
          onClick={() => setIsReportOpen(true)}
          data-testid="lookup-open-report"
        >
          {I18n.t('lookupTool.reportButton')}
        </CustomButton>
        <IconButton
          disabled={!lookupDomainResult}
          onClick={() => lookupDomainResult && lookupDomain(lookupDomainResult.fqdn)}
          data-testid="lookup-refresh"
        >
          <CachedIcon />
        </IconButton>
      </div>

      <Card className={classes.mainContainer__infoGroup}>
        <SingleElementInfo
          classes={classes}
          title={I18n.t('lookupTool.fqdn')}
          testId="lookup-result-fqdn"
        >
          {lookupDomainResult?.fqdn ?? ''}
        </SingleElementInfo>
        <SingleElementInfo
          classes={classes}
          title={I18n.t('lookupTool.categories')}
          testId="lookup-result-categories"
        >
          {lookupDomainResult === undefined
            ? ''
            : (lookupDomainResult?.categories ?? []).join(', ') || UNKNOWN}
        </SingleElementInfo>
        <SingleElementInfo
          classes={classes}
          title={I18n.t('lookupTool.categoryTypes')}
          testId="lookup-result-category-types"
        >
          {lookupDomainResult === undefined
            ? ''
            : (lookupDomainResult?.categoryTypes ?? []).join(', ') || UNKNOWN}
        </SingleElementInfo>
      </Card>

      <Card className={`${classes.mainContainer__infoGroup} ` +
                      `${getThreatClass(lookupDomainResult?.threats)}`}
      >
        <SingleElementInfo
          classes={classes}
          title={I18n.t('lookupTool.threats')}
          testId="lookup-result-threats"
        >
          {lookupDomainResult === undefined
            ? ''
            : (lookupDomainResult?.threats ?? []).join(', ') || I18n.t('lookupTool.noThreats')}
        </SingleElementInfo>
      </Card>

      <Card className={classes.mainContainer__infoGroup}>
        <SingleElementInfo
          classes={classes}
          title={I18n.t('lookupTool.parentDomain')}
          testId="lookup-result-parent-domain"
        >
          {lookupDomainResult?.parentDomain ?? ''}
        </SingleElementInfo>
        <SingleElementInfo
          classes={classes}
          title={I18n.t('lookupTool.parentCategories')}
          testId="lookup-result-parent-categories"
        >
          {lookupDomainResult === undefined
            ? ''
            : (lookupDomainResult?.parentDomainCategories ?? []).join(', ') || UNKNOWN}
        </SingleElementInfo>
      </Card>

      <Card className={classes.mainContainer__history}>
        <h2 className={classes.history__title}>
          {I18n.t('lookupTool.categoryHistory.title')}
        </h2>
        {/* @ts-ignore-next-line */}
        <ReactTable
          columns={[
            {
              Header: I18n.t('lookupTool.categoryHistory.date'),
              accessor: 'date',
              filterable: false,
              sortable: false,
              className: classes.history__date,
            },
            {
              Header: I18n.t('lookupTool.categoryHistory.category'),
              accessor: 'categories',
              Cell: categoriesRenderer,
              filterable: false,
              sortable: false,
              className: classes.history__categories,
            },
          ]}
          getTdProps={(state: any, rowInfo: any, column: any) => ({
            className: column.className,
            style: {display: 'flex'},
          })}
          getTheadThProps={(state: any, rowInfo: any, column: any) => ({
            className: column.className,
            style: {display: 'flex'},
          })}
          data={categoryHistory}
          filterable={false}
          sortable={false}
          showPagination={false}
          resizable={false}
          pageSize={Math.max(defaultHistoryPageSize, categoryHistory.length)}
          defaultPageSize={defaultHistoryPageSize}
          data-testId="lookup-category-history-table"
        />
      </Card>

      <ReportDomainModal
        isOpen={isReportOpen}
        setIsOpen={setIsReportOpen}
        domain={lookupDomainResult?.fqdn ?? ''}
        domainCategories={lookupDomainResult?.categories ?? []}
      />

    </Card>
  );
}

export default withStyles(style)(DomainLookup);
